import {
  buildGrowthEfficiencyDealEffort,
  getGrowthEfficiencyDealsInRange,
  type GrowthEfficiencyCalibration,
  type GrowthEfficiencyDealEffort,
  type GrowthEfficiencyDealInput,
  type GrowthEfficiencyDimension,
  type GrowthEfficiencyModelConfig,
  type GrowthEfficiencyPeriodRange,
} from "./growthEfficiency";
import { SALES_ECONOMICS_READINESS_COPY as COPY } from "./salesEconomicsCopy";
import type { SalesEconomicsStageEffortAnalysis } from "./salesEconomicsStageEffort";
import type { SalesEconomicsRiskEffort } from "./salesEconomicsRiskEffort";

export const salesEconomicsReadinessPolicy = {
  minimumCoverage: 0.8,
  minimumDeals: 30,
  minimumOutcomeDeals: 5,
  maximumUnregisteredMeetingShare: 0.5,
} as const;

export type SalesEconomicsReadinessIssue =
  | "incomplete_data" | "small_sample" | "sparse_meetings" | "mixed_pipelines" | "missing_pipeline";

export type SalesEconomicsOutcomeCoverage = {
  population: number;
  complete: number;
  coverage: number | null;
  noMeetings: number;
  noSellerEmails: number;
  noActivities: number;
};

export type SalesEconomicsPeriodReadiness = {
  population: number;
  complete: number;
  won: SalesEconomicsOutcomeCoverage;
  lost: SalesEconomicsOutcomeCoverage;
  issues: SalesEconomicsReadinessIssue[];
  ready: boolean;
};

export type SalesEconomicsReadiness = {
  current: SalesEconomicsPeriodReadiness;
  comparison: SalesEconomicsPeriodReadiness;
  issues: SalesEconomicsReadinessIssue[];
  ready: boolean;
  pipelines: Array<{
    id: string;
    label: string;
    current: SalesEconomicsPeriodReadiness;
    comparison: SalesEconomicsPeriodReadiness;
    ready: boolean;
    selectable: boolean;
  }>;
  groups: Record<string, SalesEconomicsPeriodReadiness>;
  dealRows: Array<{
    id: number | string;
    name: string;
    pipeline: string;
    outcome: "Won" | "Lost";
    complete: boolean;
    salesHours: number | null;
    meetings: number | null;
    sellerEmails: number | null;
    value: number;
  }>;
};

function outcomeCoverage(
  population: GrowthEfficiencyDealInput[],
  efforts: GrowthEfficiencyDealEffort[],
): SalesEconomicsOutcomeCoverage {
  return {
    population: population.length,
    complete: efforts.length,
    coverage: population.length ? efforts.length / population.length : null,
    noMeetings: efforts.filter(deal => deal.meetings === 0).length,
    noSellerEmails: efforts.filter(deal => deal.sellerEmails === 0).length,
    noActivities: efforts.filter(deal => deal.meetings === 0 && deal.sellerEmails === 0).length,
  };
}

function assessPeriod(
  population: GrowthEfficiencyDealInput[],
  effortsById: Map<string, GrowthEfficiencyDealEffort>,
): SalesEconomicsPeriodReadiness {
  const efforts = population.flatMap(deal => {
    const effort = effortsById.get(String(deal.id));
    return effort ? [effort] : [];
  });
  const won = outcomeCoverage(population.filter(deal => deal.isWon), efforts.filter(deal => deal.isWon));
  const lost = outcomeCoverage(population.filter(deal => deal.isLost), efforts.filter(deal => deal.isLost));
  const issues: SalesEconomicsReadinessIssue[] = [];
  if ([won, lost].some(group => group.population > 0 && (group.coverage ?? 0) < salesEconomicsReadinessPolicy.minimumCoverage)) {
    issues.push("incomplete_data");
  }
  if (efforts.length < salesEconomicsReadinessPolicy.minimumDeals
    || [won, lost].some(group => group.complete < salesEconomicsReadinessPolicy.minimumOutcomeDeals)) {
    issues.push("small_sample");
  }
  if ([won, lost].some(group => group.complete > 0
    && group.noMeetings / group.complete > salesEconomicsReadinessPolicy.maximumUnregisteredMeetingShare)) {
    issues.push("sparse_meetings");
  }
  return { population: population.length, complete: efforts.length, won, lost, issues, ready: issues.length === 0 };
}

export function buildSalesEconomicsReadiness({
  deals, currentRange, comparisonRange, calibration, config, breakdownDimension,
}: {
  deals: GrowthEfficiencyDealInput[];
  currentRange: GrowthEfficiencyPeriodRange;
  comparisonRange: GrowthEfficiencyPeriodRange;
  calibration: GrowthEfficiencyCalibration;
  config: GrowthEfficiencyModelConfig;
  breakdownDimension: GrowthEfficiencyDimension | null;
}): SalesEconomicsReadiness {
  const currentDeals = getGrowthEfficiencyDealsInRange(deals, currentRange);
  const comparisonDeals = getGrowthEfficiencyDealsInRange(deals, comparisonRange);
  const population = [...new Map([...currentDeals, ...comparisonDeals].map(deal => [String(deal.id), deal])).values()];
  const effortsById = new Map(population.flatMap(deal => {
    const effort = buildGrowthEfficiencyDealEffort(deal, calibration, config);
    return effort ? [[String(deal.id), effort] as const] : [];
  }));
  const current = assessPeriod(currentDeals, effortsById);
  const comparison = assessPeriod(comparisonDeals, effortsById);
  const pipelineIds = [...new Set(population.map(deal => deal.pipelineId))];
  const missingPipelineIds = new Set(population.filter(deal =>
    ["not-specified", ""].includes(deal.pipelineId) || !deal.dimensions.pipeline || deal.dimensions.pipeline === "Not specified",
  ).map(deal => deal.pipelineId));
  const pipelines = pipelineIds.map(id => {
    const pipelineCurrent = assessPeriod(currentDeals.filter(deal => deal.pipelineId === id), effortsById);
    const pipelineComparison = assessPeriod(comparisonDeals.filter(deal => deal.pipelineId === id), effortsById);
    return {
      id,
      label: missingPipelineIds.has(id) ? COPY.unspecifiedPipeline : population.find(deal => deal.pipelineId === id)!.dimensions.pipeline!,
      current: pipelineCurrent,
      comparison: pipelineComparison,
      ready: pipelineCurrent.ready && pipelineComparison.ready && !missingPipelineIds.has(id),
      selectable: !missingPipelineIds.has(id),
    };
  }).sort((a, b) => a.label.localeCompare(b.label));
  const issues = [...new Set([...current.issues, ...comparison.issues])];
  if (pipelineIds.length > 1) issues.push("mixed_pipelines");
  if (missingPipelineIds.size) issues.push("missing_pipeline");
  const groups: SalesEconomicsReadiness["groups"] = {};
  if (breakdownDimension) {
    for (const label of new Set(currentDeals.map(deal => deal.dimensions[breakdownDimension] ?? "Not specified"))) {
      groups[label] = assessPeriod(currentDeals.filter(deal => (deal.dimensions[breakdownDimension] ?? "Not specified") === label), effortsById);
    }
  }
  return {
    current, comparison, issues, ready: issues.length === 0 && pipelineIds.length === 1,
    pipelines, groups,
    dealRows: currentDeals.map(deal => {
      const effort = effortsById.get(String(deal.id));
      return {
        id: deal.id, name: deal.name ?? `Deal ${deal.id}`,
        pipeline: missingPipelineIds.has(deal.pipelineId) ? COPY.unspecifiedPipeline : deal.dimensions.pipeline!,
        outcome: deal.isWon ? "Won" : "Lost", complete: Boolean(effort),
        salesHours: effort?.totalEffort ?? null,
        meetings: deal.meetings,
        sellerEmails: deal.sellerEmails,
        value: deal.value,
      };
    }),
  };
}

export function salesEconomicsReadinessMessages(issues: SalesEconomicsReadinessIssue[]) {
  const messages: Record<SalesEconomicsReadinessIssue, string> = {
    incomplete_data: COPY.incomplete,
    small_sample: COPY.smallSample,
    sparse_meetings: COPY.sparseMeetings,
    mixed_pipelines: COPY.mixedPipelines,
    missing_pipeline: COPY.missingPipeline,
  };
  return issues.map(issue => messages[issue]);
}

export function isSalesEconomicsStageReady(
  stage: SalesEconomicsStageEffortAnalysis | null,
  readiness: SalesEconomicsReadiness | null,
) {
  const period = readiness?.pipelines.find(pipeline => pipeline.id === stage?.pipelineId)?.current;
  return Boolean(stage && period?.ready
    && stage.populationDeals === period.population
    && stage.eligibleDeals >= salesEconomicsReadinessPolicy.minimumDeals
    && stage.wonDeals >= salesEconomicsReadinessPolicy.minimumOutcomeDeals
    && stage.lostDeals >= salesEconomicsReadinessPolicy.minimumOutcomeDeals
    && period.won.population > 0 && stage.wonDeals / period.won.population >= salesEconomicsReadinessPolicy.minimumCoverage
    && period.lost.population > 0 && stage.lostDeals / period.lost.population >= salesEconomicsReadinessPolicy.minimumCoverage);
}

export function isSalesEconomicsRiskReady(analysis: SalesEconomicsRiskEffort | null) {
  if (!analysis?.hasRiskHistory) return false;
  const patterns = analysis.patterns;
  const population = patterns.activityCompleteDeals + patterns.excludedActivityDeals;
  return population > 0
    && patterns.activityCompleteDeals >= salesEconomicsReadinessPolicy.minimumDeals
    && patterns.activityCompleteDeals / population >= salesEconomicsReadinessPolicy.minimumCoverage
    && patterns.quadrants.every(pattern => pattern.count === 0 || pattern.count >= salesEconomicsReadinessPolicy.minimumOutcomeDeals);
}
